import React from "react";
import { motion } from "framer-motion";
import { Heart, ShoppingBag, Sparkles, Users, Flower, Headphones } from "lucide-react";

export default function About() {
  return (
    <div className="overflow-hidden bg-pink-100">
      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[70vh] flex items-center justify-center bg-gray-900 text-white">
        <img
          src="https://ik.imagekit.io/rasoipro212/hair?updatedAt=1757952853959"
          alt="About Banner - Elegant Hair Accessory"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <motion.div
          className="relative z-10 text-center px-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-6xl font-bold">About Accessories Girly</h1>
          <p className="mt-3 text-lg md:text-xl text-gray-200">
            Unveiling the essence of femininity through exquisite accessories designed for the modern woman.
          </p>
        </motion.div>
      </section>

      {/* Our Story */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <img
            src="https://ik.imagekit.io/rasoipro212/im?updatedAt=1757952853170"
            alt="Playful Headband Style"
            className="rounded-2xl shadow-lg"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold mb-4">Our Story</h2>
          <p className="text-gray-700 leading-relaxed">
            Founded in 2015, Accessories Girly emerged from a deep-seated passion for empowering women through timeless and trendy accessories. What began as a small boutique venture in a vibrant city has blossomed into a globally recognized brand, celebrated for its commitment to quality, innovation, and inclusivity.
          </p>
          <p className="mt-4 text-gray-700 leading-relaxed">
            Our journey is rooted in the belief that every accessory tells a story—one of confidence, elegance, and personal expression. We meticulously curate collections that blend contemporary trends with classic designs, ensuring each piece resonates with the diverse lifestyles of our customers. From delicate jewelry that adds a sparkle to your daily ensemble to versatile handbags that combine functionality with flair, our products are crafted to enhance your unique style.
          </p>
          <p className="mt-4 text-gray-700 leading-relaxed">
            Over the years, we've expanded our reach, partnering with skilled artisans worldwide to source sustainable materials and ethical production methods. Today, Accessories Girly stands as a beacon of feminine empowerment, trusted by thousands of women who seek not just accessories, but companions in their journey of self-expression.
          </p>
        </motion.div>
      </section>

      {/* Our Inspiration */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center bg-white">
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold mb-4">Our Inspiration</h2>
          <p className="text-gray-700 leading-relaxed">
            At Accessories Girly, we draw inspiration from the beauty of nature and the vibrancy of everyday life. Our designs celebrate the joy of femininity, incorporating elements like floral motifs and playful accents that evoke a sense of freedom and happiness.
          </p>
          <p className="mt-4 text-gray-700 leading-relaxed">
            We believe in creating pieces that not only adorn but also inspire. Whether it's a flower crown for a special occasion or a chic headband for casual outings, our accessories are designed to bring out the best in you, fostering a connection between style and self-confidence.
          </p>
          <p className="mt-4 text-gray-700 leading-relaxed">
            Our creative process involves collaborating with talented designers who infuse each collection with fresh ideas, ensuring that our offerings remain at the forefront of fashion while staying true to our core values of elegance and accessibility.
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <img
            src="https://ik.imagekit.io/rasoipro212/lovable-white-woman-flower-wreath-dancing-blue-wall.jpg?updatedAt=1757952702652"
            alt="Flower Crown Elegance"
            className="rounded-2xl shadow-lg"
          />
        </motion.div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-pink-100 py-16 px-6">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <h2 className="text-3xl font-bold">Our Mission & Vision</h2>
          <p className="mt-3 text-gray-600">
            Dedicated to transforming ordinary moments into extraordinary expressions of style and grace.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <motion.div
            className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition"
            whileHover={{ scale: 1.03 }}
          >
            <Heart className="w-10 h-10 text-pink-500 mb-3" />
            <h3 className="text-xl font-semibold mb-2">Our Mission</h3>
            <p className="text-gray-600">
              To deliver premium, affordable accessories that empower women to embrace their individuality and radiate confidence in every aspect of life. We prioritize sustainability, ethical sourcing, and exceptional customer experiences to build lasting relationships.
            </p>
          </motion.div>
          <motion.div
            className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition"
            whileHover={{ scale: 1.03 }}
          >
            <Sparkles className="w-10 h-10 text-yellow-500 mb-3" />
            <h3 className="text-xl font-semibold mb-2">Our Vision</h3>
            <p className="text-gray-600">
              To lead the global accessories market as the premier destination for innovative, versatile, and timeless pieces that inspire women to celebrate their femininity and achieve their fullest potential.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold">Why Choose Accessories Girly?</h2>
          <p className="mt-3 text-gray-600">Experience the difference in quality, style, and service that sets us apart.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: <ShoppingBag className="w-10 h-10 text-purple-600" />,
              title: "Curated Trendy Collections",
              desc: "Our expert team scours the fashion world to curate collections that blend the latest trends with enduring classics, ensuring you always have access to pieces that elevate your wardrobe.",
            },
            {
              icon: <Users className="w-10 h-10 text-green-600" />,
              title: "Customer-Centric Approach",
              desc: "We place our customers at the heart of everything we do, offering personalized recommendations, seamless shopping experiences, and dedicated support to make your journey with us unforgettable.",
            },
            {
              icon: <Heart className="w-10 h-10 text-red-500" />,
              title: "Uncompromising Quality",
              desc: "Each accessory undergoes rigorous quality assurance processes, using premium materials and craftsmanship to guarantee durability, comfort, and a flawless finish that stands the test of time.",
            },
            {
              icon: <Flower className="w-10 h-10 text-pink-600" />,
              title: "Sustainable Practices",
              desc: "Committed to the planet, we prioritize eco-friendly materials and ethical manufacturing, allowing you to indulge in beauty without compromising your values.",
            },
            {
              icon: <Headphones className="w-10 h-10 text-blue-600" />,
              title: "Exceptional Service",
              desc: "From fast shipping and easy returns to expert styling advice, our team is here to provide a superior service experience that exceeds your expectations.",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              className="p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.2 }}
            >
              {item.icon}
              <h3 className="text-xl font-semibold mt-3">{item.title}</h3>
              <p className="text-gray-600 mt-2">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-pink-300 text-white py-16 px-6 text-center">
        <motion.h2
          className="text-3xl md:text-4xl font-bold mb-4"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          Join the Accessories Girly Family
        </motion.h2>
        <p className="mb-6 max-w-2xl mx-auto text-lg">
          Discover a world of elegance and style tailored just for you. Become part of a community that celebrates femininity and individuality.
        </p>
        <motion.a
          href="/products"
          className="inline-block px-6 py-3 bg-white text-pink-600 font-semibold rounded-full shadow-lg hover:bg-gray-100 transition"
          whileHover={{ scale: 1.05 }}
        >
          Explore Our Collections
        </motion.a>
      </section>
    </div>
  );
}